import { createI18n } from 'vue-i18n'
import cs from './locales/cs.json'
import en from './locales/en.json'

type schema = typeof en

const LOCALE_STORAGE_KEY = 'beerview_locale'
const SUPPORTED_LOCALES = ['en', 'cs'] as const
type SupportedLocale = (typeof SUPPORTED_LOCALES)[number]

// Get browser locale
function getBrowserLocale(): string {
  const navigatorLocale = navigator.languages
    ? navigator.languages[0]
    : navigator.language

  if (!navigatorLocale) {
    return 'en'
  }

  // Get language code (e.g., 'cs' from 'cs-CZ')
  const languageCode = navigatorLocale.trim().split(/[-_]/)[0]

  return languageCode
}

// Get locale from localStorage
function getStoredLocale(): SupportedLocale | null {
  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY)
    if (stored && SUPPORTED_LOCALES.includes(stored as SupportedLocale)) {
      return stored as SupportedLocale
    }
  } catch (e) {
    // localStorage might not be available
    console.warn('Failed to read locale from localStorage:', e)
  }
  return null
}

// Get locale from server (passed via initial auth data)
function getServerLocale(): SupportedLocale | null {
  try {
    const initialAuth = (window as any).__INITIAL_AUTH__
    if (
      initialAuth?.user?.locale &&
      SUPPORTED_LOCALES.includes(initialAuth.user.locale)
    ) {
      return initialAuth.user.locale as SupportedLocale
    }
  } catch (e) {
    console.warn('Failed to read locale from server data:', e)
  }
  return null
}

// Determine initial locale with priority:
// 1. Server-stored user preference (for logged-in users)
// 2. localStorage preference (for all users)
// 3. Browser language detection
// 4. Fallback to English
function getInitialLocale(): SupportedLocale {
  // Priority 1: Server locale (user's saved preference)
  const serverLocale = getServerLocale()
  if (serverLocale) {
    return serverLocale
  }

  // Priority 2: localStorage
  const storedLocale = getStoredLocale()
  if (storedLocale) {
    return storedLocale
  }

  // Priority 3: Browser locale
  const browserLocale = getBrowserLocale()
  if (SUPPORTED_LOCALES.includes(browserLocale as SupportedLocale)) {
    return browserLocale as SupportedLocale
  }

  // Priority 4: Fallback to English
  return 'en'
}

// Save locale to localStorage
export function saveLocalePreference(locale: SupportedLocale): void {
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale)
  } catch (e) {
    console.warn('Failed to save locale to localStorage:', e)
  }
}

export const i18n = createI18n<[schema], 'en' | 'cs'>({
  legacy: false, // Use Composition API mode
  locale: getInitialLocale(),
  fallbackLocale: 'en',
  messages: {
    en,
    cs,
  },
})

export { SUPPORTED_LOCALES }
export type { SupportedLocale }
export default i18n
